import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Barbell, MagnifyingGlass } from "@phosphor-icons/react";
import MuscleMappingsPanel from "@/components/app/MuscleMappingsPanel";
import ExerciseCard from "@/components/app/exercise-library/ExerciseCard";
import ExerciseDialog from "@/components/app/exercise-library/ExerciseDialog";
import ExercisePreviewLightbox from "@/components/app/exercise-library/ExercisePreviewLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Petto", "Schiena", "Spalle", "Braccia", "Gambe", "Glutei", "Addome", "Cardio", "Full Body", "Altro"];
const MUSCLE_GROUPS = ["Pettorali", "Dorsali", "Deltoidi", "Bicipiti", "Tricipiti", "Quadricipiti", "Femorali", "Polpacci", "Glutei", "Addominali", "Trapezio", "Avambracci", "Altro"];
const EQUIPMENTS = ["Bilanciere", "Manubri", "Cavi", "Macchina", "Corpo libero", "Kettlebell", "Elastici", "TRX", "Palla medica", "Altro"];

const EMPTY_FORM = {
  name: "",
  category: "",
  muscle_group: "",
  secondary_muscle_groups: [],
  equipment: "",
  description: "",
};

export default function ExerciseLibraryPage() {
  const [exercises, setExercises] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterMuscle, setFilterMuscle] = useState("all");
  const [filterEquipment, setFilterEquipment] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mediaBlobs, setMediaBlobs] = useState({});
  const [uploading, setUploading] = useState(null);
  const [previewExercise, setPreviewExercise] = useState(null);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchExercises = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/exercises`);
      setExercises(data);
    } catch (err) {
      console.error("[ExerciseLibrary] fetch failed", err);
      toast.error("Errore nel caricamento esercizi");
    }
  }, []);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  // Lazy-load: media blobs are NOT prefetched in batch anymore. Only the
  // exercise selected for the lightbox triggers a fetch, keeping the grid
  // light both visually and network-wise.
  const ensureMediaBlob = useCallback(async (ex) => {
    if (!ex?.media_path) return null;
    if (mediaBlobs[ex.id]) return mediaBlobs[ex.id];
    try {
      const response = await axios.get(`${API}/files/${ex.media_path}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(response.data);
      setMediaBlobs((prev) => ({ ...prev, [ex.id]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.warn("[ExerciseLibrary] media load failed", ex.id, err);
      return null;
    }
  }, [mediaBlobs]);

  // When the user opens the preview, hydrate the blob if not yet cached.
  useEffect(() => {
    if (previewExercise) {
      ensureMediaBlob(previewExercise);
    }
  }, [previewExercise, ensureMediaBlob]);

  const filtered = exercises.filter((ex) => {
    const matchSearch = ex.name.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === "all" || ex.category === filterCategory;
    const matchMuscle = filterMuscle === "all" || ex.muscle_group === filterMuscle;
    const matchEquipment = filterEquipment === "all" || ex.equipment === filterEquipment;
    return matchSearch && matchCategory && matchMuscle && matchEquipment;
  });

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    // Sync back-compat singular with first list item so old consumers keep working.
    const payload = {
      ...form,
      secondary_muscle_group: form.secondary_muscle_groups?.[0] || "",
    };
    try {
      if (editing) {
        await axios.put(`${API}/exercises/${editing.id}`, payload);
        toast.success("Esercizio aggiornato");
      } else {
        await axios.post(`${API}/exercises`, payload);
        toast.success("Esercizio aggiunto alla libreria");
      }
      setShowDialog(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchExercises();
    } catch (err) {
      console.error("[ExerciseLibrary] save failed", err);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo esercizio dalla libreria?")) return;
    try {
      await axios.delete(`${API}/exercises/${id}`);
      toast.success("Esercizio eliminato");
      if (mediaBlobs[id]) URL.revokeObjectURL(mediaBlobs[id]);
      fetchExercises();
    } catch (err) {
      console.error("[ExerciseLibrary] delete failed", err);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleUploadMedia = async (exerciseId, file) => {
    setUploading(exerciseId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/exercises/${exerciseId}/media`, fd);
      toast.success("Media caricato");
      if (mediaBlobs[exerciseId]) URL.revokeObjectURL(mediaBlobs[exerciseId]);
      setMediaBlobs((prev) => {
        const n = { ...prev };
        delete n[exerciseId];
        return n;
      });
      fetchExercises();
    } catch (err) {
      console.error("[ExerciseLibrary] upload failed", err);
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteMedia = async (exerciseId) => {
    try {
      await axios.delete(`${API}/exercises/${exerciseId}/media`);
      toast.success("Media eliminato");
      if (mediaBlobs[exerciseId]) URL.revokeObjectURL(mediaBlobs[exerciseId]);
      setMediaBlobs((prev) => {
        const n = { ...prev };
        delete n[exerciseId];
        return n;
      });
      fetchExercises();
    } catch (err) {
      console.error("[ExerciseLibrary] delete media failed", err);
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEdit = (ex) => {
    setEditing(ex);
    const secondaries = Array.isArray(ex.secondary_muscle_groups) && ex.secondary_muscle_groups.length > 0
      ? ex.secondary_muscle_groups
      : (ex.secondary_muscle_group ? [ex.secondary_muscle_group] : []);
    setForm({
      name: ex.name,
      category: ex.category || "",
      muscle_group: ex.muscle_group || "",
      secondary_muscle_groups: secondaries,
      equipment: ex.equipment || "",
      description: ex.description || "",
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowDialog(true);
  };

  const toggleExpand = (id) => {
    setExpandedExercise((prev) => (prev === id ? null : id));
  };

  return (
    <div data-testid="exercise-library-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            LIBRERIA ESERCIZI
          </h1>
          <p className="text-zinc-500 text-sm mt-1 tracking-wide">
            {exercises.length} esercizi in libreria
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
          data-testid="add-exercise-library-button"
        >
          <Plus size={18} className="mr-2" /> Nuovo Esercizio
        </Button>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca esercizi..."
            className="pl-10 bg-[#0A0A0A] border-[#262626] focus:border-[#007AFF] text-white placeholder:text-zinc-600"
            data-testid="search-exercises-input"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger
            className="w-full sm:w-44 bg-[#0A0A0A] border-[#262626] text-white"
            data-testid="exercise-category-filter"
          >
            <SelectValue placeholder="Tutte le categorie" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#262626]">
            <SelectItem value="all" className="text-white">Tutte le categorie</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterMuscle} onValueChange={setFilterMuscle}>
          <SelectTrigger
            className="w-full sm:w-44 bg-[#0A0A0A] border-[#262626] text-white"
            data-testid="exercise-muscle-filter"
          >
            <SelectValue placeholder="Tutti i muscoli" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#262626]">
            <SelectItem value="all" className="text-white">Tutti i muscoli</SelectItem>
            {MUSCLE_GROUPS.map((m) => (
              <SelectItem key={m} value={m} className="text-white">{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterEquipment} onValueChange={setFilterEquipment}>
          <SelectTrigger
            className="w-full sm:w-44 bg-[#0A0A0A] border-[#262626] text-white"
            data-testid="exercise-equipment-filter"
          >
            <SelectValue placeholder="Tutti gli attrezzi" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#262626]">
            <SelectItem value="all" className="text-white">Tutti gli attrezzi</SelectItem>
            {EQUIPMENTS.map((e) => (
              <SelectItem key={e} value={e} className="text-white">{e}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Custom Muscle Mappings (coach-only, affects volume summaries) */}
      <div className="mb-6">
        <MuscleMappingsPanel />
      </div>

      {/* Exercise Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((ex, i) => (
            <ExerciseCard
              key={ex.id}
              ex={ex}
              index={i}
              mediaBlobUrl={mediaBlobs[ex.id]}
              uploading={uploading}
              expanded={expandedExercise === ex.id}
              onToggleExpand={toggleExpand}
              onUploadMedia={handleUploadMedia}
              onDeleteMedia={handleDeleteMedia}
              onEdit={openEdit}
              onDelete={handleDelete}
              onPreview={setPreviewExercise}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-[#171717] border border-[#262626] rounded-lg">
          <Barbell size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">{search ? "Nessun esercizio trovato" : "Libreria vuota"}</p>
          <p className="text-zinc-600 text-sm mt-1">
            Aggiungi esercizi per averli disponibili nelle schede
          </p>
        </div>
      )}

      <ExercisePreviewLightbox
        exercise={previewExercise}
        mediaBlobUrl={previewExercise ? mediaBlobs[previewExercise.id] : null}
        onClose={() => setPreviewExercise(null)}
      />

      <ExerciseDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        editing={editing}
        form={form}
        setForm={setForm}
        categories={CATEGORIES}
        muscleGroups={MUSCLE_GROUPS}
        equipments={EQUIPMENTS}
        onSave={handleSave}
      />
    </div>
  );
}
